import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Database, AlertTriangle, Download, ExternalLink, RefreshCw } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";

interface CroDataEntry {
    ClientID: string;
    SentaTitle: string;
    SentaCompanyNumber: string;
    CroCompanyNumber?: string;
    CompanyBusinessIndicator?: string;
    CompanyName?: string;
    CompanyAddress1?: string;
    CompanyAddress2?: string;
    CompanyAddress3?: string;
    CompanyAddress4?: string;
    RegistrationDate?: string;
    CompanyStatusDescription?: string;
    CompanyStatusDate?: string;
    LastAnnualReturnDate?: string;
    NextAnnualReturnDate?: string;
    LastAccountsDate?: string;
    CompanyTypeDescription?: string;
    CompanyTypeCode?: string;
    CompanyStatusCode?: string;
    PlaceOfBusiness?: string;
    Eircode?: string;
    UpdatedAt?: string;
    Error?: string;
}

interface Report {
    name: string;
    timestamp: string;
    data: CroDataEntry[];
}

interface ApiResponse {
    reports: Report[];
    configured: boolean;
    message?: string;
}

const tableStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
            minHeight: "48px",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const today = () => new Date().toISOString().split("T")[0];

const CroDataDump: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [reports, setReports] = useState<Report[]>([]);
    const [selected, setSelected] = useState<string>("");
    const [configured, setConfigured] = useState<boolean>(true);
    const [serverMessage, setServerMessage] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [companyTypeFilter, setCompanyTypeFilter] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (accounts.length === 0) return;
        setLoading(true);
        try {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const res = await fetch(`${getApiBaseUrl()}/api/cro-data-dump`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: ApiResponse = await res.json();
            const sorted = (data.reports || []).slice().sort(
                (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            setReports(sorted);
            setSelected(sorted[0]?.name || "");
            setConfigured(!!data.configured);
            setServerMessage(data.message || null);
            setError(null);
        } catch (err: any) {
            setError(err.message || "Failed to load CRO data dump");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [instance, accounts]);

    const currentReport = useMemo(
        () => reports.find(r => r.name === selected) || null,
        [reports, selected]
    );

    const { companyTypes, statuses } = useMemo(() => {
        const ct = new Set<string>();
        const st = new Set<string>();
        if (currentReport) {
            currentReport.data.forEach(e => {
                if (e.CompanyTypeDescription) ct.add(e.CompanyTypeDescription);
                if (e.CompanyStatusDescription) st.add(e.CompanyStatusDescription);
            });
        }
        return { companyTypes: Array.from(ct).sort(), statuses: Array.from(st).sort() };
    }, [currentReport]);

    const filtered = useMemo(() => {
        if (!currentReport) return [];
        let data = currentReport.data;
        if (search.trim()) {
            const s = search.toLowerCase();
            data = data.filter(row =>
                Object.values(row).some(v => v && v.toString().toLowerCase().includes(s))
            );
        }
        if (companyTypeFilter !== "all") {
            data = data.filter(row => row.CompanyTypeDescription === companyTypeFilter);
        }
        if (statusFilter !== "all") {
            data = data.filter(row => row.CompanyStatusDescription === statusFilter);
        }
        return data;
    }, [currentReport, search, companyTypeFilter, statusFilter]);

    const columns: TableColumn<CroDataEntry>[] = [
        {
            name: "Client ID",
            selector: row => row.ClientID,
            sortable: true,
            cell: row => (
                <a
                    href={`https://kinore.senta.co/c/v/${row.ClientID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--accent-blue)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                    {row.ClientID}
                    <ExternalLink size={12} />
                </a>
            ),
            width: "150px",
        },
        { name: "Company", selector: row => row.SentaTitle, sortable: true },
        { name: "CRN", selector: row => row.SentaCompanyNumber || "—", sortable: true, width: "120px" },
        { name: "Type", selector: row => row.CompanyTypeDescription || "—", sortable: true },
        { name: "Status", selector: row => row.CompanyStatusDescription || "—", sortable: true },
        { name: "Registered", selector: row => row.RegistrationDate || "—", sortable: true, width: "120px" },
        { name: "Last AR", selector: row => row.LastAnnualReturnDate || "—", sortable: true, width: "120px" },
        { name: "Next AR", selector: row => row.NextAnnualReturnDate || "—", sortable: true, width: "120px" },
    ];

    const downloadCSV = () => {
        const headers = [
            "Client ID", "Company", "Senta CRN", "CRO CRN", "Company Name", "Type", "Status",
            "Registered", "Status Date", "Last AR", "Next AR", "Last Accounts",
            "Address 1", "Address 2", "Address 3", "Address 4", "Eircode", "Place of Business",
        ];
        const rows = filtered.map(d => [
            d.ClientID, d.SentaTitle, d.SentaCompanyNumber || "", d.CroCompanyNumber || "",
            d.CompanyName || "", d.CompanyTypeDescription || "", d.CompanyStatusDescription || "",
            d.RegistrationDate || "", d.CompanyStatusDate || "", d.LastAnnualReturnDate || "",
            d.NextAnnualReturnDate || "", d.LastAccountsDate || "",
            d.CompanyAddress1 || "", d.CompanyAddress2 || "", d.CompanyAddress3 || "", d.CompanyAddress4 || "",
            d.Eircode || "", d.PlaceOfBusiness || "",
        ]);
        const esc = (v: any) => {
            const s = String(v ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [headers.join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `cro-data-dump-${today()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Database size={20} />
                    <h1>CRO Data Dump</h1>
                </div>
            </div>

            {!configured && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>{serverMessage || "CRO API credentials are not configured on the server. The report is showing no data."}</span>
                </div>
            )}

            {error && (
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading data: {error}</span>
                </div>
            )}

            <div className="stats-grid stats-grid-3">
                <div className="stat-card">
                    <div className="stat-icon"><Database size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{reports.length}</div>
                        <div className="stat-label">Snapshots</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><Database size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{filtered.length}</div>
                        <div className="stat-label">Companies</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><Database size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value" style={{ fontSize: "0.95rem" }}>
                            {currentReport ? new Date(currentReport.timestamp).toLocaleDateString("en-IE") : "—"}
                        </div>
                        <div className="stat-label">Snapshot Date</div>
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    className="filter-select"
                    value={selected}
                    onChange={e => setSelected(e.target.value)}
                    disabled={reports.length === 0}
                >
                    {reports.length === 0 && <option value="">No snapshots</option>}
                    {reports.map(r => (
                        <option key={r.name} value={r.name}>
                            {new Date(r.timestamp).toLocaleString("en-IE")}
                        </option>
                    ))}
                </select>
                <select
                    className="filter-select"
                    value={companyTypeFilter}
                    onChange={e => setCompanyTypeFilter(e.target.value)}
                >
                    <option value="all">All Types</option>
                    {companyTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                >
                    <option value="all">All Statuses</option>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={fetchData} className="download-btn" title="Refresh">
                    <RefreshCw size={16} /> Refresh
                </button>
                <button onClick={downloadCSV} className="download-btn" title="Download CSV">
                    <Download size={16} /> Export CSV
                </button>
            </div>

            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filtered}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    noDataComponent={<div className="no-data">No CRO data in this snapshot.</div>}
                    customStyles={tableStyles}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        Full CRO (Companies Registration Office) record snapshot for every Senta client with a
                        registered company number. Shows status, type, registration date, and annual-return dates.
                    </>
                }
                badge={configured ? "PRODUCTION DATA" : "NOT CONFIGURED"}
                badgeColor={configured ? undefined : "#f59e0b"}
                sections={[
                    {
                        title: "Backend endpoint",
                        items: [
                            { name: "GET /api/cro-data-dump", description: "Returns the list of CRO data dump snapshots. Empty until credentials are configured." },
                        ],
                    },
                    {
                        title: "Required environment variables",
                        items: [
                            { name: "CRO_USERNAME", description: "CRO Open Services account username/email." },
                            { name: "CRO_API_KEY", description: "CRO Open Services API key (HTTP Basic auth)." },
                        ],
                    },
                    {
                        title: "Database tables read (after sync wired)",
                        items: [
                            { name: "senta_companies", description: "Source of the company numbers to look up." },
                            { name: "cro_snapshots (planned)", description: "Will hold CRO API responses keyed by snapshot timestamp once the sync job is enabled." },
                        ],
                    },
                ]}
            />
        </div>
    );
};

export default CroDataDump;
